import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ExportModal from '../common/ExportModal';
import UploadModal from '../common/UploadModal';
import EditableCell from '../common/EditableCell';
import FilterDropdown from '../common/FilterDropdown';
import { EXPORT_CONFIGS } from '../../config/exportConfigs';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';
import useResizableColumns from '../../hooks/useResizableColumns';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import TariffAnalyticsDashboard from './TariffAnalyticsDashboard';

// Column definitions for the tariff table
const COLUMNS = [
  { key: 'material_category', label: 'Material Category', type: 'text', width: 140 },
  { key: 'part_number', label: 'Part Number', type: 'text', width: 120 },
  { key: 'ottav_type', label: 'Ottav Type', type: 'text', width: 100 },
  { key: 'hts', label: 'HTS', type: 'text', width: 100 },
  { key: 'coo', label: 'COO', type: 'text', width: 60 },
  { key: 'country', label: 'Country', type: 'text', width: 100 },
  { key: 'mill', label: 'Mill', type: 'text', width: 100 },
  { key: 'exceptions', label: 'Exceptions', type: 'text', width: 120 },
  { key: 'ad_case_number', label: 'AD Case #', type: 'text', width: 100 },
  { key: 'ad_percent_nov_13', label: 'AD %', type: 'number', width: 70 },
  { key: 'cv_case_number', label: 'CV Case #', type: 'text', width: 100 },
  { key: 'cv_percent_nov_13', label: 'CV %', type: 'number', width: 70 },
  { key: 'section_232_percent_nov_13', label: 'Sec 232 %', type: 'number', width: 90 },
  { key: 'section_301_percent_nov_13', label: 'Sec 301 %', type: 'number', width: 90 },
  { key: 'reciprocal_tariffs_percent_nov_13', label: 'Reciprocal %', type: 'number', width: 100 },
  { key: 'max_ad_percent_per_country', label: 'Max AD %', type: 'number', width: 90 },
  { key: 'max_cv_percent_per_country', label: 'Max CV %', type: 'number', width: 90 },
  { key: 'max_section_232_percent_per_country', label: 'Max 232 %', type: 'number', width: 90 },
  { key: 'max_section_301_percent_per_country', label: 'Max 301 %', type: 'number', width: 90 },
  { key: 'max_reciprocal_tariffs_percent_per_country', label: 'Max Recip %', type: 'number', width: 100 },
];

// Format value for display (number columns show 2 decimal places)
const formatTariffValue = (val, column) => {
  if (val === null || val === undefined || val === '') return '-';
  if (column.type === 'number') return Number(val).toFixed(2);
  return val;
};

const TariffLog = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [tariffs, setTariffs] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const downloadedJobsRef = useRef(new Set());

  const fetchTariffsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchTariffsRef.current) fetchTariffsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('tariffLog', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Use reusable hooks for filtering and sorting
  const filterHook = useTableFilter({ columns: COLUMNS, items: tariffs });
  const sortHook = useTableSort({ columns: COLUMNS });

  // Resizable columns
  const defaultWidths = useMemo(() => {
    const widths = {};
    COLUMNS.forEach(col => { widths[col.key] = col.width; });
    return widths;
  }, []);
  const { getColumnWidth, getResizeHandleProps } = useResizableColumns(
    'tariffLog_columnWidths',
    defaultWidths
  );

  const fetchTariffs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/tariff'
      );

      if (response.data.success) {
        setTariffs(response.data.data || []);
      } else {
        setError('Failed to load tariff data');
      }
    } catch (err) {
      console.error('Error fetching tariff data:', err);
      setError('Failed to load tariff data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchTariffsRef.current = fetchTariffs; }, [fetchTariffs]);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);


  // Export handlers
  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      setShowExportModal(false);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/tariff/export', null, syncHeaders());
      if (response.data.success) {
        setExportJobId(response.data.jobId);
        showToast(`Export started! Job ID: ${response.data.jobId}`, 'success');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      setIsExporting(false);
      showToast(`Export error: ${error.message}`, 'error');
    }
  };

  const handleExportJobComplete = (jobData) => {
    showToast(`Export completed! ${jobData.processed_rows} records exported.`, 'success');
    setIsExporting(false);
    setExportJobId(null);
    if (jobData.job_id && !downloadedJobsRef.current.has(jobData.job_id)) {
      downloadedJobsRef.current.add(jobData.job_id);
      window.open(`/api/download/${jobData.job_id}`, '_blank');
    }
  };

  const handleExportJobError = (errorMessage) => {
    showToast(`Export failed: ${errorMessage}`, 'error');
    setIsExporting(false);
    setExportJobId(null);
  };

  const handleSaveCell = async (rowId, columnKey, newValue) => {
    setSavingRows(prev => new Set(prev).add(rowId));

    try {
      const column = COLUMNS.find(c => c.key === columnKey);
      let processedValue = newValue;
      
      if (column?.type === 'number') {
        processedValue = newValue === '' ? null : Number(newValue);
      }

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/tariff/${rowId}`,
        { [columnKey]: processedValue },
        syncHeaders()
      );

      if (response.data.success) {
        setTariffs(prev => prev.map(t => 
          t.id === rowId ? { ...t, [columnKey]: processedValue } : t
        ));
        showToast('Saved successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving cell:', err);
      showToast(`Save failed: ${err.message}`, 'error');
      throw err;
    } finally {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/tariff',
        newRow,
        syncHeaders()
      );

      if (response.data.success) {
        setTariffs(prev => [response.data.data, ...prev]);
        setNewRow({});
        setIsAddingNew(false);
        showToast('New record added successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to add record');
      }
    } catch (err) {
      console.error('Error adding new record:', err);
      showToast(`Failed to add record: ${err.message}`, 'error');
    }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/tariff/${rowId}`,
        null,
        syncHeaders()
      );

      if (response.data.success) {
        setTariffs(prev => prev.filter(t => t.id !== rowId));
        showToast('Record deleted successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && filterHook.openFilterColumn) {
        filterHook.setOpenFilterColumn(null);
        filterHook.setFilterSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook]);

  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  // Filter and sort tariffs
  const filteredTariffs = useMemo(() => {
    let result = [...tariffs];
    
    // Global search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(t => COLUMNS.some(col => { 
        const val = t[col.key]; 
        return val !== null && val !== undefined && String(val).toLowerCase().includes(search); 
      }));
    }
    
    // Apply column filters
    result = filterHook.applyFilters(result);
    
    // Apply sorting
    result = sortHook.applySorting(result);
    
    return result;
  }, [tariffs, searchTerm, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{
          display: 'inline-block',
          width: '48px',
          height: '48px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '1rem', color: '#666' }}>Loading tariff data...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <strong>⚠️ Error:</strong> {error}
        <button
          onClick={fetchTariffs}
          style={{
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#333' }}>💰 Tariff Data</h3>
          <span style={{ 
            backgroundColor: '#17a2b8', 
            color: 'white', 
            padding: '4px 12px', 
            borderRadius: '20px',
            fontSize: '0.85rem'
          }}>
            {filteredTariffs.length} records
          </span>
          
          {/* Active Filters Bar */}
          {hasActiveFilters && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '12px', color: '#065f46', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold' }}>Filters:</span>
              {searchTerm && <span style={{ padding: '2px 6px', backgroundColor: '#10b981', color: 'white', borderRadius: '3px', fontSize: '11px' }}>Search: "{searchTerm}"</span>}
              {filterHook.activeFilters.map((f, idx) => (
                <span key={idx} style={{ padding: '2px 6px', backgroundColor: '#10b981', color: 'white', borderRadius: '3px', fontSize: '11px' }}>
                  {f.label}: {f.type === 'values' ? `${f.count} selected` : f.type === 'text' ? `${f.mode} "${f.value}"` : f.description || f.value}
                </span>
              ))}
              <button onClick={handleClearAll} style={{ padding: '2px 8px', backgroundColor: '#065f46', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>✕ Clear All</button>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px 8px 36px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                width: '200px',
                fontSize: '0.9rem'
              }}
            />
            <span style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d'
            }}>🔍</span>
          </div>

          {/* Analytics Button */}
          <button
            onClick={() => setShowAnalytics(true)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
          >
            📊 Analytics
          </button>
          {/* Export Button */}
          <button
            onClick={() => setShowExportModal(true)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
          >
            📤 Export
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
          >
            📥 Import
          </button>
          <button
            onClick={() => setIsAddingNew(true)}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
          >
            ➕ Add New
          </button>
          <button
            onClick={fetchTariffs}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: '#e7f3ff',
        padding: '0.75rem 1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        fontSize: '0.85rem',
        color: '#0056b3'
      }}>
        💡 <strong>Tip:</strong> Click any cell to edit. Changes are saved automatically.
      </div>

      {/* Add New Row Form */}
      {isAddingNew && (
        <div style={{
          backgroundColor: '#fff3cd',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #ffc107'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#856404' }}>Add New Tariff Record</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
            {COLUMNS.slice(0, 10).map(col => (
              <div key={col.key}>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                  {col.label}
                </label>
                <input
                  type={col.type === 'number' ? 'number' : 'text'}
                  step={col.type === 'number' ? '0.01' : undefined}
                  value={newRow[col.key] || ''}
                  onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleAddNewRow}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ✓ Save
            </button>
            <button
              onClick={() => { setIsAddingNew(false); setNewRow({}); }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        maxHeight: 'calc(100vh - 350px)',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: 'white'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.85rem',
          minWidth: '2200px'
        }}>
          <thead style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#343a40',
            color: 'white',
            zIndex: 10
          }}>
            <tr>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'center',
                width: '60px',
                borderRight: '1px solid #138496',
                position: 'sticky',
                left: 0,
                backgroundColor: '#343a40',
                zIndex: 11
              }}>
                Actions
              </th>
              {COLUMNS.map(col => {
                const colWidth = getColumnWidth(col.key, col.width);
                return (
                <th key={col.key} style={{ padding: '8px 4px', textAlign: col.type === 'number' ? 'right' : 'left', width: colWidth, minWidth: colWidth, maxWidth: colWidth, borderRight: '1px solid #495057', whiteSpace: 'nowrap', position: 'relative', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start', gap: '4px' }}>
                    <span onClick={() => sortHook.handleSort(col.key)} style={{ cursor: 'pointer', flex: 1 }} title="Click to sort">
                      {col.label}
                      {sortHook.sortColumn === col.key && <span style={{ marginLeft: '4px' }}>{sortHook.sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </span>
                    <span 
                      onClick={(e) => { e.stopPropagation(); filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); }} 
                      style={{ cursor: 'pointer', opacity: filterHook.hasColumnFilter(col.key) ? 1 : 0.5, fontSize: '0.75rem', color: filterHook.hasColumnFilter(col.key) ? '#10b981' : 'white' }} 
                      title="Filter"
                    >
                      🔽
                    </span>
                  </div>
                  {filterHook.openFilterColumn === col.key && (
                    <FilterDropdown
                      column={col}
                      uniqueValues={filterHook.getUniqueValues(col.key)}
                      selectedValues={filterHook.selectedValues[col.key] || []}
                      textFilter={filterHook.textFilters[col.key] || null}
                      numericFilter={filterHook.numericFilters[col.key] || null}
                      dateFilter={filterHook.dateFilters[col.key] || null}
                      filterSearch={filterHook.filterSearch}
                      onToggleValue={filterHook.toggleValue}
                      onSelectAll={filterHook.selectAll}
                      onClearColumn={filterHook.clearColumn}
                      onApplyTextFilter={filterHook.applyTextFilter}
                      onApplyNumericFilter={filterHook.applyNumericFilter}
                      onApplyDateFilter={filterHook.applyDateFilter}
                      onClose={() => {
                        filterHook.setOpenFilterColumn(null);
                        filterHook.setFilterSearch('');
                      }}
                      onSearchChange={filterHook.setFilterSearch}
                    />
                  )}
                  <div {...getResizeHandleProps(col.key, colWidth)} />
                </th>
              );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredTariffs.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} style={{ 
                  padding: '3rem', 
                  textAlign: 'center', 
                  color: '#6c757d' 
                }}>
                  {searchTerm ? 'No matching records found' : 'No tariff records. Click "Add New" to create one.'}
                </td>
              </tr>
            ) : (
              filteredTariffs.map((tariff, index) => (
                <tr 
                  key={tariff.id} 
                  style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    borderBottom: '1px solid #dee2e6'
                  }}
                >
                  <td style={{ 
                    padding: '4px 8px', 
                    textAlign: 'center',
                    borderRight: '1px solid #eee',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    zIndex: 5
                  }}>
                    <button
                      onClick={() => handleDeleteRow(tariff.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title="Delete row"
                    >
                      🗑️
                    </button>
                  </td>
                  {COLUMNS.map(col => {
                    const colWidth = getColumnWidth(col.key, col.width);
                    return (
                    <td 
                      key={col.key} 
                      style={{ 
                        padding: '4px 8px',
                        textAlign: col.type === 'number' ? 'right' : 'left',
                        borderRight: '1px solid #eee',
                        backgroundColor: savingRows.has(tariff.id) ? '#fff3cd' : 'inherit',
                        width: colWidth,
                        minWidth: colWidth,
                        maxWidth: colWidth
                      }}
                    >
                      <EditableCell
                        value={tariff[col.key]}
                        row={tariff}
                        column={col}
                        onSave={handleSaveCell}
                        disabled={false}
                        formatValue={formatTariffValue}
                      />
                    </td>
                  );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Export Job Monitor */}
      {exportJobId && (
        <JobStatusMonitor
          jobId={exportJobId}
          onComplete={handleExportJobComplete}
          onError={handleExportJobError}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          {...EXPORT_CONFIGS.tariff}
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
        />
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          fetchTariffs();
          setShowUploadModal(false);
        }}
        onUploadError={(msg) => showToast(msg, 'error')}
        config={UPLOAD_CONFIGS.TARIFF}
      />

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <TariffAnalyticsDashboard
          data={tariffs}
          onClose={() => setShowAnalytics(false)}
        />
      )}

    </div>
  );
};

export default TariffLog;

